import angular, { isNumber } from "angular";
import {
  vsName as csvWriterServiceName,
  CsvWriter,
  CsvColumnConfig,
} from "../../../records/csv/csvWriter";
import {
  booleanFormatterFactory,
  currencyFormatter,
} from "../../../records/csv/formatters";
import { IQuery } from "../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";

export class DailyReadingsViewController {
  static $inject: string[] = [
    "date",
    "$scope",
    "$q",
    csvWriterServiceName,
    "$http",
    ServerUtilityName,
  ];

  datePickerOpened: boolean = false;
  date: Date;

  private readonly aggregate: IProgressiveAggregate = {
    completeMachineCount: 0,
    completeReadingCount: 0,
    liability: 0,
    machineCount: 0,
    readingCount: 0,
    liabilityChange: 0,
  };
  constructor(
    date: Date,
    $scope: angular.IScope,
    private readonly $q: angular.IQService,
    private readonly csvWriter: CsvWriter,
    private readonly $http: angular.IHttpService,
    private readonly serverUtility: ServerUtility
  ) {
    this.date = date;

    $scope.$watch(
      () => this.date,
      (newDate: Date, oldDate: Date) =>
        this.dateChanged($scope, newDate, oldDate)
    );
  }

  dateChanged($scope: angular.IScope, newDate: Date, oldDate: Date): void {
    const jackpotAggregatePromise = this.updateJackpotAggregateInfo(newDate);
    const machineAggregatePromise = this.updateMachineAggregateInfo(newDate);

    if (newDate?.getTime() === oldDate?.getTime()) {
      return;
    }

    this.$q.all([jackpotAggregatePromise, machineAggregatePromise]).then(() => {
      $scope.$broadcast("dateChanged", newDate);
    });
  }

  getReconciliationReport(): void {
    this.$http({
      method: "POST",
      url: "/api/machines/progressives/ProgressiveDailyPeriod/actions/GetReconciliationReport",
      data: {
        Date: this.date,
      },
    }).then((result: any) => {
      const csv = this.getCsv(result.Result.Readings);
      this.csvWriter.saveFile({
        content: csv,
        filename: "Reconciliation Report.csv",
      });
    });
  }

  getPercentage(fraction: number, whole: number) {
    return Math.floor((fraction / whole) * 100.0);
  }

  private readonly _denominationMatcher: RegExp = /\$[\d\.]+|MD$/;

  private getCsv(report: any): string {
    const conditionNotMetColumn: CsvColumnConfig = {
      field: "ConditionNotMet",
      header: "Condition Not Met?",
      formatter: booleanFormatterFactory(),
    };

    const atCeilingColumn: CsvColumnConfig = {
      field: "AtCeiling",
      header: "Cap Reached?",
      formatter: (params: { row: any }) => {
        const atCeiling = params.row?.AtCeiling;
        if (atCeiling === undefined || atCeiling === null) {
          return undefined;
        }

        return atCeiling ? "Yes" : "No";
      },
    };

    const columns: CsvColumnConfig[] = [
      {
        field: "PropertyName",
        header: "Property",
      },
      {
        field: "ProgressiveMeterReadingSettingName",
        header: "Class",
      },
      {
        field: "JackpotType",
        header: "Jackpot Type",
      },
      {
        field: "Location",
      },
      {
        field: "Machine",
      },
      {
        field: "ProgressiveDefinitionGameDescription",
        header: "Denomination",
        formatter: (params: { row: any }) => {
          const { row } = params;
          const gameDescription: string =
            row.ProgressiveDefinitionGameDescription;
          if (!gameDescription) {
            return undefined;
          }

          const regexMatch = gameDescription.match(this._denominationMatcher);
          return regexMatch ? regexMatch[0] : undefined;
        },
      },
      {
        field: "ProgressiveDefinitionName",
        header: "Progressive",
      },
      {
        field: "ProgressiveDefinitionGameDescription",
        header: "Game",
      },
      {
        field: "ProgressiveDefinitionGameLevelName",
        header: "Level",
      },
      {
        field: "ProgressiveDailyReadingGameLevelReadingIdOfRecord",
        header: "Read today",
        formatter: booleanFormatterFactory(),
      },
      {
        field: "ReadingUser",
        header: "User",
      },
      {
        field: "ResetBase",
        header: "Base",
        format: "currency",
      },
      {
        field: "PriorMeterReading",
        format: "currency",
        header: "Open",
      },
      {
        field: "Hits",
      },
      {
        field: "Liability",
        format: "currency",
        header: "Close",
      },
      conditionNotMetColumn,
      atCeilingColumn,
      {
        field: "difference",
        header: "Apply",
        formatter: (params: { row: any }) => {
          const { Liability, PriorMeterReading } = params.row;
          if (Liability === undefined || PriorMeterReading === undefined) {
            return undefined;
          }

          return currencyFormatter({
            cellValue: Liability - PriorMeterReading,
            row: params.row,
          });
        },
      },
      {
        field: "ChangePercentage",
        header: "Percent Change",
        format: "percentage",
      },
      {
        field: "Empty",
        header: "Machine/Ticket#",
      },
      {
        field: "Empty",
        header: "Customer Contributed",
      },
    ];

    const firstConditionalReading = report.find((l: any) =>
      isNumber(l.OptionalBelow)
    );
    if (!firstConditionalReading) {
      columns.splice(columns.indexOf(conditionNotMetColumn), 1);
    }

    const reportAtCeiling = report.find(
      (l: any) => l.AtCeiling === true || l.AtCeiling === false
    );
    if (!reportAtCeiling) {
      columns.splice(columns.indexOf(atCeilingColumn), 1);
    }

    return this.csvWriter.getCsv({ columns }, report);
  }

  private updateJackpotAggregateInfo(date: Date): angular.IPromise<void> {
    const completedCountTerm =
      "count(progressivedailyreadinggamelevelreadingidofrecord)";
    const totalCountTerm = "count(*)";
    const sumLiabilityTerm = "sum(liability)";
    const sumChangeTerm = "sum(change)";

    const query: IQuery = {
      ProgressiveDailyPeriodDate: date,
      Take: 0,
      Include: `${totalCountTerm},${completedCountTerm},${sumLiabilityTerm},${sumChangeTerm}`,
    };

    return this.serverUtility
      .queryWrapped({
        url: "/api/machines/progressives/ProgressiveDailyReading/GameLevel",
        query,
      })
      .then((queryResponse) => {
        const meta = queryResponse.Meta;
        this.aggregate.completeReadingCount =
          Number.parseInt(meta[completedCountTerm]) || 0;
        this.aggregate.readingCount =
          Number.parseInt(meta[totalCountTerm]) || 0;
        this.aggregate.liability =
          Number.parseFloat(meta[sumLiabilityTerm]) || 0;
        this.aggregate.liabilityChange =
          Number.parseFloat(meta[sumChangeTerm]) || 0;
      });
  }

  private updateMachineAggregateInfo(
    date: Date
  ): angular.IPromise<[void, void]> {
    const query: IQuery = {
      ProgressiveDailyPeriodDate: date,
      Take: 0,
      Include: "count(*)",
    };

    const url = "/api/machines/progressives/ProgressiveDailyMachine";
    const totalCountPromise = this.serverUtility
      .queryWrapped({ url, query })
      .then((queryResponse) => {
        const meta = queryResponse.Meta;
        this.aggregate.machineCount = Number.parseInt(meta["count(*)"]) || 0;
      });

    const doneCountPromise = this.serverUtility
      .queryWrapped({
        url,
        query: {
          ...query,
          Complete: true,
        },
      })
      .then((queryResponse) => {
        this.aggregate.completeMachineCount = queryResponse.Total;
      });

    return this.$q.all([totalCountPromise, doneCountPromise]);
  }

  showDatepicker($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.datePickerOpened = true;
  }
}

interface IProgressiveAggregate {
  readingCount: number;
  machineCount: number;
  completeReadingCount: number;
  completeMachineCount: number;
  liability: number;
  liabilityChange: number;
}
